import { useConfirmation } from '@/hooks/UseConfirmation'
import { TRASH_RETENTION_DAYS } from '@/constants/Trash'

/**
 * Item type for content deletion confirmations
 * Note: id is string to support both playbook integer IDs (as strings) and block UUIDs
 */
export interface ContentItem {
	id: string
	name: string
	type: string
}

/**
 * Folder type for folder deletion confirmations
 */
export interface FolderItem {
	id: string
	name: string
}

/**
 * Parameters for the confirmations hook
 */
export interface UseContentManagerConfirmationsParams {
	onDeleteItem: (item: ContentItem) => Promise<void>
	onPermanentDelete: (item: ContentItem) => Promise<void>
	onRestore: (item: ContentItem) => Promise<void>
	onEmptyTrash: () => Promise<void>
	onDeleteFolder: (folder: FolderItem) => Promise<void>
}

/**
 * Hook for managing ContentManager confirmation dialogs
 *
 * Consolidates all confirmation dialog logic into a single hook
 * to reduce boilerplate in the main component.
 *
 * @example
 * const confirmations = useContentManagerConfirmations({
 *   onDeleteItem: async (item) => { ... },
 *   onPermanentDelete: async (item) => { ... },
 *   onRestore: async (item) => { ... },
 *   onEmptyTrash: async () => { ... },
 *   onDeleteFolder: async (folder) => { ... },
 * })
 *
 * // Usage:
 * confirmations.deleteConfirm.prompt({ id: 1, name: 'Test', type: 'playbook' })
 */
export function useContentManagerConfirmations({
	onDeleteItem,
	onPermanentDelete,
	onRestore,
	onEmptyTrash,
	onDeleteFolder,
}: UseContentManagerConfirmationsParams) {
	/**
	 * Soft delete - moves item to trash
	 */
	const deleteConfirm = useConfirmation<ContentItem>({
		title: 'Delete Item',
		description: (item) =>
			`Are you sure you want to delete "${item.name}"? This will move it to trash where it can be restored within ${TRASH_RETENTION_DAYS} days.`,
		confirmText: 'Delete',
		variant: 'destructive',
		onConfirm: onDeleteItem,
	})

	/**
	 * Permanent delete - removes item forever
	 */
	const permanentDeleteConfirm = useConfirmation<ContentItem>({
		title: 'Permanently Delete',
		description: (item) =>
			`Are you sure you want to permanently delete "${item.name}"? This action cannot be undone.`,
		confirmText: 'Permanently Delete',
		variant: 'destructive',
		onConfirm: onPermanentDelete,
	})

	/**
	 * Restore - restores item from trash
	 */
	const restoreConfirm = useConfirmation<ContentItem>({
		title: 'Restore Item',
		description: (item) => `Restore "${item.name}" from trash?`,
		confirmText: 'Restore',
		variant: 'default',
		onConfirm: onRestore,
	})

	/**
	 * Empty trash - permanently deletes all trashed items
	 */
	const emptyTrashConfirm = useConfirmation<Record<string, never>>({
		title: 'Empty Trash',
		description: () =>
			'Are you sure you want to permanently delete all items in trash? This action cannot be undone.',
		confirmText: 'Empty Trash',
		variant: 'destructive',
		onConfirm: onEmptyTrash,
	})

	/**
	 * Delete folder - removes folder (contents stay)
	 */
	const deleteFolderConfirm = useConfirmation<FolderItem>({
		title: 'Delete Folder',
		description: (folder) =>
			`Delete "${folder.name}"? Content in this folder will not be deleted.`,
		confirmText: 'Delete',
		variant: 'destructive',
		onConfirm: onDeleteFolder,
	})

	return {
		deleteConfirm,
		permanentDeleteConfirm,
		restoreConfirm,
		emptyTrashConfirm,
		deleteFolderConfirm,
	}
}

/**
 * Return type for the confirmations hook
 */
export type UseContentManagerConfirmationsReturn = ReturnType<typeof useContentManagerConfirmations>
